import { MedplumClient } from "@medplum/core";
import { ChargeItem } from "@medplum/fhirtypes";

import { EncounterServicesStatusCode } from "@/billing/constants/encounter-services-status";
import { tagServicesStatusAfterWrite } from "@/billing/services/encounter-services-status";
import {
  EncounterServicesStatusCodeValue,
  ServicesStatusTaggedResult,
} from "@/billing/types/encounter-services-status";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { resolveEncounterServicesStatus } from "../utils/encounter-charge-items";

import { searchEncounterChargeItems } from "./encounter-charge-items-search";

/**
 * Tags an encounter `services-not-sent` after a capture wrote new charges.
 *
 * The charges are read first, and the tag is skipped when a send already moved every one of them
 * on. Otherwise the tag is written and then confirmed against the charges, the same way a send
 * confirms its own tag. See {@link confirmServicesStatus}.
 *
 * @param encounterId The encounter the capture wrote charges for.
 * @param data What the capture wrote, returned unchanged either way.
 * @returns The capture's success result, with `servicesStatusTagFailure` set when a tag write failed.
 */
export async function tagCapturedServicesStatus<Data>(
  medplum: MedplumClient,
  encounterId: string,
  data: Data,
): Promise<ServicesStatusTaggedResult<Data>> {
  const chargeItems = await readChargeItems(medplum, encounterId);

  if (
    chargeItems &&
    resolveEncounterServicesStatus(chargeItems) === EncounterServicesStatusCode.Sent
  ) {
    return { ok: true, data };
  }

  const result = await tagServicesStatusAfterWrite(
    medplum,
    encounterId,
    EncounterServicesStatusCode.NotSent,
    data,
  );

  return confirmServicesStatus(medplum, encounterId, EncounterServicesStatusCode.NotSent, result);
}

/**
 * Tags an encounter `services-sent` after a send moved its charges on, then confirms the tag
 * against the charges. See {@link confirmServicesStatus}.
 *
 * @param encounterId The encounter whose charges were sent.
 * @param data What the send produced, returned unchanged either way.
 * @returns The send's success result, with `servicesStatusTagFailure` set when a tag write failed.
 */
export async function tagSentServicesStatus<Data>(
  medplum: MedplumClient,
  encounterId: string,
  data: Data,
): Promise<ServicesStatusTaggedResult<Data>> {
  const result = await tagServicesStatusAfterWrite(
    medplum,
    encounterId,
    EncounterServicesStatusCode.Sent,
    data,
  );

  return confirmServicesStatus(medplum, encounterId, EncounterServicesStatusCode.Sent, result);
}

/**
 * How many times a writer reads the charges again after its own tag write before it stops. Each
 * extra round only happens when another writer changed the charges in between, so the cap is never
 * reached in normal use.
 */
const MAX_SERVICES_STATUS_CONFIRMATIONS = 3;

/**
 * Text returned as `servicesStatusTagFailure` when a `services-sent` tag could not be confirmed.
 */
const UNCONFIRMED_SENT_FAILURE =
  "Could not read the encounter's charges to confirm services-sent, so it was tagged services-not-sent";

/**
 * Reads the encounter's charges after a tag write and corrects the tag when the charges call for
 * the other code.
 *
 * A capture and a send each change the charges first and write the tag second, and either can
 * finish while the other is still running. So the tag written last can come from a read that is
 * already out of date: a send can tag `services-sent` after a capture added a `planned` charge, or
 * a capture can tag `services-not-sent` after a send moved its charge on. So every tag write,
 * including a correction, is followed by another read of the charges. The writer that writes last
 * has read the charges after its own write, so the last tag matches the charges.
 *
 * When the charges cannot be read, the encounter is left `services-not-sent`, which keeps it on the
 * Missing Services queue. A `services-sent` tag that cannot be confirmed is replaced with
 * `services-not-sent`, and the result reports it as a tag failure so the caller can tell the user.
 * Nothing is corrected when a tag write failed, or when the encounter has no active charge left.
 */
async function confirmServicesStatus<Data>(
  medplum: MedplumClient,
  encounterId: string,
  writtenCode: EncounterServicesStatusCodeValue,
  result: ServicesStatusTaggedResult<Data>,
): Promise<ServicesStatusTaggedResult<Data>> {
  let confirmedCode = writtenCode;
  let confirmedResult = result;

  for (let round = 0; round < MAX_SERVICES_STATUS_CONFIRMATIONS; round++) {
    if (confirmedResult.servicesStatusTagFailure) return confirmedResult;

    const chargeItems = await readChargeItems(medplum, encounterId);

    if (!chargeItems) {
      return confirmedCode === EncounterServicesStatusCode.Sent
        ? tagUnconfirmedSentAsNotSent(medplum, encounterId, confirmedResult.data)
        : confirmedResult;
    }

    const currentCode = resolveEncounterServicesStatus(chargeItems);

    if (!currentCode || currentCode === confirmedCode) return confirmedResult;

    confirmedResult = await tagServicesStatusAfterWrite(
      medplum,
      encounterId,
      currentCode,
      confirmedResult.data,
    );
    confirmedCode = currentCode;
  }

  return confirmedResult;
}

/**
 * Replaces a `services-sent` tag that could not be confirmed with `services-not-sent`, and reports
 * it as a tag failure. A failure of that write is reported instead.
 */
async function tagUnconfirmedSentAsNotSent<Data>(
  medplum: MedplumClient,
  encounterId: string,
  data: Data,
): Promise<ServicesStatusTaggedResult<Data>> {
  const fallback = await tagServicesStatusAfterWrite(
    medplum,
    encounterId,
    EncounterServicesStatusCode.NotSent,
    data,
  );

  return {
    ...fallback,
    servicesStatusTagFailure: fallback.servicesStatusTagFailure ?? UNCONFIRMED_SENT_FAILURE,
  };
}

/**
 * Reads every charge of the encounter, or returns `undefined` when the read fails. A failed read is
 * reported to Sentry with the encounter id only.
 */
async function readChargeItems(
  medplum: MedplumClient,
  encounterId: string,
): Promise<ChargeItem[] | undefined> {
  return searchEncounterChargeItems(medplum, encounterId).catch((error: unknown) => {
    captureExceptionWithoutPhi(
      "Failed to read the encounter's charges while tagging its services status",
      error,
      { encounterId },
    );
    return undefined;
  });
}
